import React, { useState } from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  chakra,
  TableCaption,
  Box,
  Select,
  Input,
  IconButton,
  Flex,
  useDisclosure,
  Skeleton,
} from '@chakra-ui/react';
import {
  TriangleDownIcon,
  TriangleUpIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
} from '@chakra-ui/icons';
import { useTable, useSortBy, useFilters, usePagination } from 'react-table';
import { GoChevronRight, GoChevronLeft } from 'react-icons/go';
import { billingsColumns } from '../constants';
import SendInvoiceModal from './SendInvoiceModal';

const BillingsTable = ({
  tableData,
  totalPages = 0,
  onPageChange,
  isLoading = false,
}) => {
  const [selectedInvoiceRow, setSelectedInvoiceRow] = useState(-1);
  const didMountRef = React.useRef(false);

  const {
    isOpen: sendInvoiceModalIsOpen,
    onOpen: sendInvoiceModalOnOpen,
    onClose: sendInvoiceModalOnClose,
  } = useDisclosure();

  const data = React.useMemo(() => tableData || [], [tableData]);

  // Define a default UI for filtering
  function DefaultColumnFilter() {
    return <></>;
  }

  const defaultColumn = React.useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const columns = React.useMemo(
    () => billingsColumns(sendInvoiceModalOnOpen, setSelectedInvoiceRow),
    [sendInvoiceModalOnOpen]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      initialState: {
        pageSize: 25,
        sortBy: [{ id: 'dateTime', desc: true }],
      },
      manualPagination: true,
      pageCount: totalPages,
      autoResetPage: false,
    },
    useFilters,
    useSortBy,
    usePagination
  );

  React.useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true; // skip first run (mount)
      return;
    }
    if (typeof onPageChange === 'function') {
      onPageChange({ pageIndex: pageIndex + 1, pageSize });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, pageSize]);

  return (
    <Box overflowX="auto">
      <Table
        {...getTableProps()}
        variant="striped"
        colorScheme="gray"
        size="md"
      >
        <TableCaption placement="bottom">
          <Flex justifyContent={'center'} alignContent={'center'}>
            <IconButton
              size="md"
              aria-label="Go to first page"
              variant="link"
              onClick={() => gotoPage(0)}
              isDisabled={isLoading || !canPreviousPage}
              icon={<ArrowLeftIcon />}
            />

            <IconButton
              size="md"
              aria-label="Previous page"
              icon={<GoChevronLeft />}
              onClick={() => previousPage()}
              isDisabled={isLoading || !canPreviousPage}
              variant="link"
            />

            <chakra.span mx={3}>
              Page{' '}
              <strong>
                {pageIndex + 1} of {pageOptions.length}
              </strong>
            </chakra.span>

            <IconButton
              size="md"
              aria-label="Next page"
              icon={<GoChevronRight />}
              onClick={() => nextPage()}
              isDisabled={isLoading || !canNextPage}
              variant="link"
            />

            <IconButton
              size="md"
              aria-label="Go to last page"
              variant="link"
              onClick={() => gotoPage(pageCount - 1)}
              isDisabled={isLoading || !canNextPage}
              icon={<ArrowRightIcon />}
            />

            <chakra.span>
              {' '}
              | Go to page:{' '}
              <Input
                size="xs"
                w={10}
                type="number"
                defaultValue={pageIndex + 1}
                onChange={e => {
                  const page = e.target.value ? Number(e.target.value) - 1 : 0;
                  gotoPage(page);
                }}
                isDisabled={isLoading}
              />
            </chakra.span>

            <Select
              size="xs"
              ml={5}
              maxW={'100px'}
              value={pageSize}
              onChange={e => {
                const newSize = Number(e.target.value);
                setPageSize(newSize);
                gotoPage(0);
              }}
              isDisabled={isLoading}
            >
              {[10, 25, 50].map(ps => (
                <option key={ps} value={ps}>
                  Show {ps}
                </option>
              ))}
            </Select>
          </Flex>
        </TableCaption>

        <Thead>
          {headerGroups.map(headerGroup => (
            <Tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <Th {...column.getHeaderProps()} isNumeric={column.isNumeric}>
                  <chakra.span {...column.getSortByToggleProps()}>
                    {column.render('Header')}
                    <chakra.span pl="4">
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <TriangleDownIcon aria-label="sorted descending" />
                        ) : (
                          <TriangleUpIcon aria-label="sorted ascending" />
                        )
                      ) : null}
                    </chakra.span>
                  </chakra.span>
                  <div>{column.canFilter ? column.render('Filter') : null}</div>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {isLoading
            ? Array.from({ length: pageSize }).map((_, i) => (
                <Tr key={`skeleton-row-${i}`}>
                  {(headerGroups[0]?.headers || []).map((col, j) => (
                    <Td
                      key={`skeleton-cell-${i}-${j}`}
                      isNumeric={col.isNumeric}
                    >
                      <Skeleton height="18px" my="6px" />
                    </Td>
                  ))}
                </Tr>
              ))
            : page.map(row => {
                prepareRow(row);
                return (
                  <Tr {...row.getRowProps()}>
                    {row.cells.map(cell => (
                      <Td
                        {...cell.getCellProps()}
                        isNumeric={cell.column.isNumeric}
                      >
                        {cell.render('Cell')}
                      </Td>
                    ))}
                  </Tr>
                );
              })}
        </Tbody>
      </Table>
      {sendInvoiceModalIsOpen && (
        <SendInvoiceModal
          isOpen={sendInvoiceModalIsOpen}
          onClose={sendInvoiceModalOnClose}
          selectedInvoiceRow={selectedInvoiceRow}
        />
      )}
    </Box>
  );
};

export default BillingsTable;
